import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import Popover from '../../../base/popover/components/Popover.web';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../base/ui/components/web/ContextMenuItemGroup';
import { setGifMenuVisibility } from '../../../gifs/actions';
import { isGifsMenuOpen } from '../../../gifs/functions.web';
import ReactionEmoji from '../../../reactions/components/web/ReactionEmoji';
import ReactionsMenu from '../../../reactions/components/web/ReactionsMenu';
import { getReactionsQueue } from '../../../reactions/functions.any';
import { IReactionsMenuParent } from '../../../reactions/types';
import { APPS_GRID_BUTTON_KEYS, APPS_GRID_ICON_COLORS, APPS_OVERFLOW_PANEL_HEIGHT, APPS_OVERFLOW_PANEL_WIDTH, DRAWER_MAX_HEIGHT, DRAWER_MAX_HEIGHT_FALLBACK, MORE_DRAWER_EXTENDED_APP_KEYS, MORE_DRAWER_GRID_ORDER, MORE_DRAWER_HIDDEN_KEYS, MORE_DRAWER_SHORT_LABELS } from '../../constants';
import { showOverflowDrawer } from '../../functions.web';
import logger from '../../logger';
import Drawer from './Drawer';
import JitsiPortal from './JitsiPortal';
import OverflowToggleButton from './OverflowToggleButton';
const useStyles = makeStyles()((theme, { overflowDrawer }) => {
    return {
        overflowMenuDrawer: {
            // 抽屉内由 menuBody/content 负责滚动；外层只限制高度，避免双滚动条
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            maxHeight: `calc(${DRAWER_MAX_HEIGHT_FALLBACK})`,
            minHeight: 0,
            overflow: 'hidden',
            '@supports (height: 100dvh)': {
                maxHeight: `calc(${DRAWER_MAX_HEIGHT})`
            }
        },
        contextMenu: {
            position: 'relative',
            right: 'auto',
            margin: 0,
            marginBottom: overflowDrawer ? '8px' : 0,
            maxHeight: overflowDrawer ? '100%' : APPS_OVERFLOW_PANEL_HEIGHT,
            height: overflowDrawer ? undefined : APPS_OVERFLOW_PANEL_HEIGHT,
            padding: 0,
            paddingBottom: overflowDrawer ? undefined : 0,
            minWidth: overflowDrawer ? undefined : APPS_OVERFLOW_PANEL_WIDTH,
            width: overflowDrawer ? undefined : APPS_OVERFLOW_PANEL_WIDTH,
            overflow: 'hidden',
            overflowY: 'hidden',
            display: 'flex',
            flex: overflowDrawer ? '1 1 auto' : undefined,
            flexDirection: 'column',
            minHeight: overflowDrawer ? 0 : APPS_OVERFLOW_PANEL_HEIGHT
        },
        /**
         * 始终包裹列表与反应栏的内层容器。
         * Drawer 模式下 ContextMenu 不会应用 className，须靠此层保证 flex 滚动链。
         */
        menuBody: {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            maxHeight: overflowDrawer ? '100%' : APPS_OVERFLOW_PANEL_HEIGHT,
            height: overflowDrawer ? undefined : '100%',
            minHeight: 0,
            overflow: 'hidden'
        },
        content: {
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch'
        },
        footer: {
            flexShrink: 0,
            position: 'relative'
        },
        cancelButton: {
            background: 'transparent',
            border: 'none',
            color: theme.palette.text01,
            cursor: 'pointer',
            display: 'block',
            fontSize: '1rem',
            lineHeight: 1.4,
            padding: '14px 16px max(16px, env(safe-area-inset-bottom, 0px))',
            textAlign: 'center',
            width: '100%'
        }
    };
});
const OverflowMenuButton = ({ buttons, isOpen, onToolboxEscKey, onVisibilityChange, showRaiseHandInReactionsMenu, showReactionsMenu, toolbarLabelBelow }) => {
    const overflowDrawer = useSelector(showOverflowDrawer);
    const reactionsQueue = useSelector(getReactionsQueue);
    const isGiphyVisible = useSelector(isGifsMenuOpen);
    const dispatch = useDispatch();
    const onCloseDialog = useCallback(() => {
        onVisibilityChange(false);
        if (isGiphyVisible && !overflowDrawer) {
            dispatch(setGifMenuVisibility(false));
        }
    }, [onVisibilityChange, setGifMenuVisibility, isGiphyVisible, overflowDrawer, dispatch]);
    const onOpenDialog = useCallback(() => {
        onVisibilityChange(true);
    }, [onVisibilityChange]);
    const onEscClick = useCallback((event) => {
        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            event.stopPropagation();
            onCloseDialog();
        }
    }, [onCloseDialog]);
    const toggleDialogVisibility = useCallback(() => {
        sendAnalytics(createToolbarEvent('overflow'));
        onVisibilityChange(!isOpen);
    }, [isOpen, onVisibilityChange]);
    const onCancelMoreDrawer = useCallback(() => {
        logger.info(`[手机更多] ${new Date().toISOString()} 点击取消，关闭更多抽屉`);
        onCloseDialog();
    }, [onCloseDialog]);
    const toolbarAccLabel = 'toolbar.accessibilityLabel.moreActionsMenu';
    const { t } = useTranslation();
    const { classes } = useStyles({ overflowDrawer });
    const gridKeySet = new Set(APPS_GRID_BUTTON_KEYS);
    const extendedKeySet = new Set(MORE_DRAWER_EXTENDED_APP_KEYS);
    const hiddenKeySet = new Set(MORE_DRAWER_HIDDEN_KEYS);
    const moreGridOrder = MORE_DRAWER_GRID_ORDER;
    const flatButtons = buttons
        .flatMap((group) => group)
        .filter((item) => item?.key && item.key !== 'reactions'
        && !(overflowDrawer && hiddenKeySet.has(item.key)));
    const sortMoreDrawerButtons = (items) => [...items].sort((a, b) => {
        const aIndex = moreGridOrder.indexOf(a.key);
        const bIndex = moreGridOrder.indexOf(b.key);
        const aRank = aIndex === -1 ? moreGridOrder.length : aIndex;
        const bRank = bIndex === -1 ? moreGridOrder.length : bIndex;
        return aRank - bRank;
    });
    const gridButtons = overflowDrawer
        ? sortMoreDrawerButtons(flatButtons.filter((item) => !extendedKeySet.has(item.key)))
        : flatButtons.filter((item) => gridKeySet.has(item.key));
    const listButtons = overflowDrawer
        ? []
        : flatButtons.filter((item) => !gridKeySet.has(item.key));
    const extendedButtons = overflowDrawer
        ? sortMoreDrawerButtons(flatButtons.filter((item) => extendedKeySet.has(item.key)))
        : [];
    const renderButton = (item, asGridItem) => {
        const { key, Content, ...rest } = item;
        const props = { ...rest };
        props.buttonKey = key;
        props.showLabel = true;
        if (asGridItem) {
            props.appsGridItem = true;
            if (!overflowDrawer) {
                props.backgroundColor = APPS_GRID_ICON_COLORS[key];
            }
            else if (MORE_DRAWER_SHORT_LABELS[key]) {
                props.gridLabel = t(MORE_DRAWER_SHORT_LABELS[key]);
            }
        }
        else {
            props.contextMenu = true;
        }
        return (React.createElement(Content, { ...props, key: key }));
    };
    const overflowMenu = (React.createElement(ContextMenu, { accessibilityLabel: t(toolbarAccLabel), className: classes.contextMenu, hidden: false, id: 'overflow-context-menu', inDrawer: overflowDrawer, onKeyDown: onToolboxEscKey },
        React.createElement("div", { className: classes.menuBody },
            React.createElement("div", { className: overflowDrawer
                    ? 'apps-overflow-panel apps-overflow-panel-drawer'
                    : 'apps-overflow-panel' },
                gridButtons.length > 0 && (React.createElement("div", { className: 'apps-overflow-grid' }, gridButtons.map((item) => renderButton(item, true)))),
                extendedButtons.length > 0 && (React.createElement(React.Fragment, null,
                    React.createElement("div", { "aria-hidden": true, className: 'apps-overflow-extended-title' }, t('toolbar.extendedApps')),
                    React.createElement("div", { className: 'apps-overflow-grid' }, extendedButtons.map((item) => renderButton(item, true))))),
                listButtons.length > 0 && (React.createElement("div", { className: 'apps-overflow-list' },
                    React.createElement(ContextMenuItemGroup, null, listButtons.map((item) => renderButton(item, false)))))),
            showReactionsMenu && overflowDrawer && (React.createElement("div", { className: classes.footer },
                React.createElement(ReactionsMenu, { parent: IReactionsMenuParent.OverflowDrawer, showRaisedHand: showRaiseHandInReactionsMenu }))),
            overflowDrawer && (React.createElement("button", { className: classes.cancelButton, onClick: onCancelMoreDrawer, type: 'button' }, t('dialog.Cancel'))))));
    if (overflowDrawer) {
        return (React.createElement("div", { className: 'toolbox-button-wth-dialog context-menu' },
            React.createElement(React.Fragment, null,
                React.createElement(OverflowToggleButton, { handleClick: toggleDialogVisibility, isOpen: isOpen, onKeyDown: onEscClick, toolbarLabelBelow: toolbarLabelBelow }),
                React.createElement(JitsiPortal, null,
                    React.createElement(Drawer, { isOpen: isOpen, onClose: onCloseDialog },
                        React.createElement(React.Fragment, null,
                            React.createElement("div", { className: classes.overflowMenuDrawer }, overflowMenu))),
                    showReactionsMenu && React.createElement("div", { className: 'reactions-animations-overflow-container' }, reactionsQueue.map(({ reaction, uid }, index) => (React.createElement(ReactionEmoji, { index: index, key: uid, reaction: reaction, uid: uid }))))))));
    }
    return (React.createElement("div", { className: 'toolbox-button-wth-dialog context-menu' },
        React.createElement(Popover, { content: overflowMenu, headingId: 'overflow-context-menu', onPopoverClose: onCloseDialog, onPopoverOpen: onOpenDialog, position: 'top', trigger: 'click', visible: isOpen },
            React.createElement(OverflowToggleButton, { isMenuButton: true, isOpen: isOpen, onKeyDown: onEscClick, toolbarLabelBelow: toolbarLabelBelow })),
        showReactionsMenu && React.createElement("div", { className: 'reactions-animations-container' }, reactionsQueue.map(({ reaction, uid }, index) => (React.createElement(ReactionEmoji, { index: index, key: uid, reaction: reaction, uid: uid }))))));
};
export default OverflowMenuButton;
